import React from 'react';
import {
  Row,
  FooterText,
  AboutText,
  ModalButton,
  Wrapper,
  OverrideDisclaimer,
} from './ChartFooter.style';
import {
  DialogMain,
  MetricInfoDialogInner,
  getMetricModalContent,
} from 'components/Dialogs';
import { MobileOnly, DesktopOnly } from '../Shared/Shared.style';
import { DialogProps } from './utils';
import { Region } from 'common/regions';
import type { MetricValues, Projections } from 'common/models/Projections';
import { useDialog } from 'common/hooks';
import { Metric } from 'common/metricEnum';
import { getSourcesForMetric } from 'common/utils/provenance';
import { getMetricNameExtended, getMetricStatusText } from 'common/metric';
import { EventCategory } from 'components/Analytics';
import { MarkdownContent } from 'components/Markdown';
import {
  getRegionMetricDisclaimer,
  getRegionMetricOverride,
} from 'cms-content/region-overrides';
import { getDataset, isEmpty } from 'common/models/ProjectionsPair';

const MetricModal: React.FC<DialogProps> = ({
  open,
  closeDialog,
  openDialog,
  modalContent,
  modalHeader,
}) => {
  const dialogProps = {
    open,
    closeDialog,
    header: modalHeader,
    links: modalContent.learnLink
      ? [
          {
            cta: 'Learn more',
            url: modalContent.learnLink,
            ariaLabel: `Learn more about ${modalHeader}`,
          },
        ]
      : undefined,
  };

  return (
    <>
      <ModalButton onClick={openDialog}>
        <AboutText>About this data</AboutText>
      </ModalButton>
      <DialogMain {...dialogProps}>
        <MetricInfoDialogInner modalContent={modalContent} />
      </DialogMain>
    </>
  );
};

const MetricChartFooter: React.FC<{
  metric: Metric;
  projections: Projections;
  region: Region;
  stats: MetricValues;
}> = ({ metric, projections, region, stats }) => {
  const provenance = getSourcesForMetric(
    projections.primary.annotations,
    metric,
  );
  const overrideDisclaimer = getRegionMetricDisclaimer(region, metric);
  const modalContent = getMetricModalContent(region, metric, provenance);
  const metricName = getMetricNameExtended(metric);
  const isBlocked = getRegionMetricOverride(region, metric)?.blocked;
  const timeseries = getDataset(projections.primary, metric);
  const timeseriesEmpty = isEmpty(timeseries);

  const footerText = getMetricStatusText(metric, projections);

  const [isOpen, openDialog, closeDialog] = useDialog(
    false,
    EventCategory.METRICS,
    `Footer modal: ${metricName}`,
  );

  const dialogProps = {
    open: isOpen,
    closeDialog,
    openDialog,
    modalContent,
    modalHeader: metricName,
  };

  // If the metric is blocked or the timeseries is empty, don't render the footer
  // because the chart will be blocked, and the disclaimer/footer will be displayed in its place.
  return isBlocked || timeseriesEmpty ? (
    <></>
  ) : (
    <Wrapper>
      <DesktopOnly>
        <Row>
          <FooterText>
            {footerText}
            {'   '}
            <MetricModal {...dialogProps} />
            {overrideDisclaimer && (
              <OverrideDisclaimer>
                <MarkdownContent>{overrideDisclaimer}</MarkdownContent>
              </OverrideDisclaimer>
            )}
          </FooterText>
        </Row>
      </DesktopOnly>
      <MobileOnly>
        <FooterText>
          {footerText}
          {overrideDisclaimer && (
            <OverrideDisclaimer>{overrideDisclaimer}</OverrideDisclaimer>
          )}
        </FooterText>
        <Row>
          <MetricModal {...dialogProps} />
        </Row>
      </MobileOnly>
    </Wrapper>
  );
};

export default MetricChartFooter;
